// FIG Process tree (DESIGN_SPEC §10.10, §12.7, RESTYLE §2–3). Props: FigProps { ctx, no }. Renders the tree cell (s8) and,
// unless a standalone <ProcessLens/> is mounted for the same job, the selected-process lens cell (s4 side).
//   meta(ctx) → { id: "processes", label: "Processes", count: traced processes, empty: none traced }
//   <ProcessTreeLive ctx className? />   the cockpit's compact tree: name, pid, pip, badges, one command-line row, the
//                                        lifetime to now; selecting a row unfolds its full command line. Never scored.
// treegrid keyed pid:startSeq: ↑/↓ move, → expands (then steps in), ← collapses (then steps out), Home/End,
// Enter or Space selects. A document row starts with a MAL · SUS · INFO chip when a behavior tag cites the process;
// live rows carry no severities, running processes carry a pip and lifetimes run to now.
import { useRef, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from "react";
import { CatMark, Empty, FigHead, Pip, Sev, ThreatChip, figTitleId, type Category, type SevLevel } from "@/components/ui";
import { useClock } from "@/hooks";
import { clock, count, dur, rel } from "@/lib/format";
import type { ActKind, CiteIndex, FigMeta, FigProps, ReportCtx } from "@/lib/derive/types";
import type { Job, Severity } from "@/types";
import { axisPct, isConhostOnly, treeOf, type Tree, type TreeNode } from "../derive/tree";
import { ProcessLensView, exitText, procName, resolveSelection, selectProcess, useSelectedKey, useStandaloneLens } from "./ProcessLens";

export function meta(ctx: ReportCtx): FigMeta {
  const n = treeOf(ctx).inScope;
  return { id: "processes", label: "Processes", count: n, empty: n === 0 };
}

const SEV_VAR: Record<number, string> = { 1: "var(--s-info)", 2: "var(--s-low)", 3: "var(--s-medium)", 4: "var(--s-high)", 5: "var(--s-critical)" };
const ACTS: [ActKind, Category, string][] = [
  ["load", "process", "load"],
  ["file", "file", "file"],
  ["reg", "registry", "reg"],
  ["net", "network", "net"],
  ["mutex", "memory", "mutex"],
];

type Guide = "pipe" | "blank" | "tee" | "end";
type Row = { node: TreeNode; level: number; guides: Guide[]; kids: boolean; expanded: boolean; parentKey?: string };

const css = (vars: Record<string, string | number>) => vars as CSSProperties;
const cx = (...parts: (string | false | null | undefined)[]) => parts.filter(Boolean).join(" ");

function sevOf(cite: CiteIndex | undefined, seqs: readonly (number | undefined)[]): Severity | undefined {
  let s: Severity | undefined;
  for (const q of seqs) {
    const c = q === undefined ? undefined : cite?.get(q);
    if (c && (!s || c.sev > s)) s = c.sev;
  }
  return s;
}

function rowsOf(tree: Tree, expanded: (n: TreeNode) => boolean): Row[] {
  const rows: Row[] = [];
  const visit = (key: string, level: number, trail: boolean[], last: boolean, parentKey?: string) => {
    const n = tree.nodes.get(key);
    if (!n) return;
    const kids = n.children.filter((k) => tree.nodes.has(k));
    const open = n.isContext || (kids.length > 0 && expanded(n));
    rows.push({ node: n, level, guides: level === 0 ? [] : [...trail.map((t): Guide => (t ? "pipe" : "blank")), last ? "end" : "tee"], kids: kids.length > 0 && !n.isContext, expanded: open, parentKey });
    if (open) kids.forEach((k, i) => visit(k, level + 1, level === 0 ? [] : [...trail, !last], i === kids.length - 1, key));
  };
  const top = tree.context ? [tree.context.key, ...tree.roots.filter((k) => !tree.context!.children.includes(k))] : [...tree.roots];
  for (const x of tree.order) if (tree.nodes.get(x)?.external) top.push(x);
  top.forEach((k) => visit(k, 0, [], true));
  return rows;
}

function sampleBadge(n: TreeNode, job: Job): string {
  if (job.kind === "url") return "opened the url";
  const f = (job.filename ?? "").toLowerCase();
  if (!f || !n.name || n.name.toLowerCase() === f) return "sample";
  return n.cmdline.toLowerCase().includes(f) ? "opened the sample" : "sample";
}

function Badge({ sev, live, children }: { sev?: Severity; live: boolean; children: ReactNode }) {
  const s = !live && sev && sev >= 3 ? (sev as 3 | 4 | 5) : undefined;
  return (
    <span className={cx("badge", s && `s${s}`)}>
      {s && <Sev level={s} label={false} />}
      {children}
    </span>
  );
}

function cmdText(n: TreeNode): string | null {
  if (n.isContext) return null;
  if (!n.cmdline) return n.image || null;
  if (n.args === "") return `${n.image} · no arguments`;
  if (n.args !== n.cmdline) return `${n.image} ${n.args}`;
  return n.cmdline;
}

function lifeText(n: TreeNode, live: boolean, nowT: number, collected: boolean, compact = false): string {
  if (n.endT !== undefined) return `${n.exitCode !== undefined ? `exit ${exitText(n.exitCode)}` : "exited"} · ${dur(n.endT - n.startT)}`;
  if (live) return `running${compact ? " " : " · "}${clock(Math.max(0, nowT - n.startT))}`;
  return collected ? "still running at collection" : "no exit recorded";
}

type GridProps = {
  ctx: ReportCtx;
  tree: Tree;
  compact?: boolean;
  /** the selected row's key (the lens, or the compact tree's unfolded row) */
  selected?: string;
  onSelect: (n: TreeNode) => void;
  className?: string;
};

function TreeGrid({ ctx, tree, compact = false, selected, onSelect, className }: GridProps) {
  const job = ctx.job;
  const live = ctx.mode === "live";
  const cite = live ? undefined : ctx.cite;
  const now = useClock(1000, live);
  const [overrides, setOverrides] = useState<ReadonlyMap<string, boolean>>(new Map());
  const [focusKey, setFocusKey] = useState<string | null>(null);
  const refs = useRef(new Map<string, HTMLDivElement>());

  const rootOffset = tree.context ? 1 : 0;
  const expandedOf = (n: TreeNode) =>
    overrides.get(n.key) ?? (n.depth - rootOffset < 3 && !isConhostOnly(tree, n) && n.name.toLowerCase() !== "conhost.exe");
  const rows = rowsOf(tree, expandedOf);
  const activeKey = rows.some((r) => r.node.key === focusKey) ? focusKey : rows.some((r) => r.node.key === selected) ? selected : rows[0]?.node.key;

  const nowT = now - tree.t0;
  const nowPct = axisPct(tree, nowT);
  const collected = ctx.stream.transitions.some((t) => t.state === "collecting" || t.state === "completed");

  const focusRow = (key: string | undefined) => {
    if (!key) return;
    setFocusKey(key);
    refs.current.get(key)?.focus();
  };
  const select = (n: TreeNode) => {
    onSelect(n);
    setFocusKey(n.key);
  };
  const toggle = (n: TreeNode, open: boolean) => setOverrides((m) => new Map(m).set(n.key, open));

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const el = (e.target as HTMLElement).closest<HTMLElement>("[data-key]");
    const i = el ? rows.findIndex((r) => r.node.key === el.dataset.key) : -1;
    if (i < 0 || e.altKey || e.ctrlKey || e.metaKey) return;
    const r = rows[i];
    switch (e.key) {
      case "ArrowDown":
        focusRow(rows[Math.min(rows.length - 1, i + 1)]?.node.key);
        break;
      case "ArrowUp":
        focusRow(rows[Math.max(0, i - 1)]?.node.key);
        break;
      case "Home":
        focusRow(rows[0]?.node.key);
        break;
      case "End":
        focusRow(rows[rows.length - 1]?.node.key);
        break;
      case "ArrowRight":
        if (r.kids && !r.expanded) toggle(r.node, true);
        else if (r.expanded && rows[i + 1]?.parentKey === r.node.key) focusRow(rows[i + 1].node.key);
        break;
      case "ArrowLeft":
        if (r.kids && r.expanded) toggle(r.node, false);
        else focusRow(r.parentKey);
        break;
      case "Enter":
      case " ":
        select(r.node);
        break;
      default:
        return;
    }
    e.preventDefault();
  };

  return (
    <div className={cx("tree", className)} role="treegrid" aria-label="Process tree" onKeyDown={onKeyDown}>
      {rows.map((r) => {
        const n = r.node;
        const isSel = selected === n.key;
        const gc = live ? "var(--ink-mute)" : (SEV_VAR[sevOf(cite, n.injectedBy.map((x) => x.seq)) ?? 0] ?? "var(--ink-mute)");
        const creators = [...new Set(n.injectedBy.map((x) => x.creatorPid))];
        const rules = [...new Set(n.yaraHits.map((y) => y.rule))];
        const ticks = [...n.injectedBy, ...n.injectedInto].filter((x, i, a) => a.findIndex((y) => y.seq === x.seq) === i);
        const exited = n.endT !== undefined && n.endT < tree.axis.to;
        const b = n.running ? nowPct : n.life.b;
        const acts = ACTS.filter(([k]) => n.acts[k] > 0);
        const cmd = cmdText(n);
        const threat = !live && n.badgeSev ? (Math.max(1, Math.min(5, n.badgeSev)) as SevLevel) : undefined;
        const outside = n.isContext || n.external;
        const life = (
          <div className={cx("life", n.running && "is-running")} style={css({ "--a": n.life.a, "--b": Math.max(n.life.a, b) })} aria-hidden="true">
            <b />
            {ticks.map((x) => (
              <i key={x.seq} className={live ? "bg-ink" : undefined} style={css({ "--x": axisPct(tree, x.t) })} />
            ))}
            {n.dumps.map((d) => (
              <u key={d.seq} style={css({ "--x": axisPct(tree, d.t) })} />
            ))}
            {live && <span className="now" style={css({ "--x": nowPct })} />}
          </div>
        );
        return (
          <div
            key={n.key}
            ref={(el) => {
              if (el) refs.current.set(n.key, el);
              else refs.current.delete(n.key);
            }}
            className={cx(
              "node",
              outside && "is-context",
              exited && "is-exited",
              compact && "grid-cols-[auto_minmax(0,1fr)_auto] max-[720px]:grid-cols-[auto_minmax(0,1fr)_auto]",
            )}
            role="row"
            aria-level={r.level + 1}
            aria-expanded={r.kids ? r.expanded : undefined}
            aria-selected={isSel}
            tabIndex={n.key === activeKey ? 0 : -1}
            data-key={n.key}
            onClick={() => select(n)}
            onFocus={(e) => {
              if (e.target === e.currentTarget) setFocusKey(n.key);
            }}
          >
            <span className="guides" aria-hidden="true">
              {r.guides.map((g, gi) => {
                const own = gi === r.guides.length - 1 && n.injectedBy.length > 0;
                return <span key={gi} className={cx("g", g !== "blank" && g, own && "inj")} style={own ? css({ "--gc": gc }) : undefined} />;
              })}
            </span>
            <div className={cx("node-main", compact && "py-1.5")} role="gridcell">
              <div className="node-l1">
                {threat && <ThreatChip sev={threat} title={`Cited by a ${["info", "low", "medium", "high", "critical"][threat - 1]} behavior tag`} />}
                <span className="node-img" translate="no">
                  {procName(n, job)}
                </span>
                <span className="node-pid">{n.pid}</span>
                {n.running && <Pip />}
                {n.isSample && <span className="badge sample">{sampleBadge(n, job)}</span>}
                {n.isContext && <span className="badge">context · not traced</span>}
                {n.external && <span className="badge">not traced</span>}
                {n.dropped && (
                  <Badge live={live} sev={sevOf(cite, [n.dropSeq, n.startSeq])}>
                    dropped by {n.dropped.byPid}
                  </Badge>
                )}
                {creators.map((pid) => (
                  <Badge key={pid} live={live} sev={sevOf(cite, n.injectedBy.filter((x) => x.creatorPid === pid).map((x) => x.seq))}>
                    thread from {pid}
                  </Badge>
                ))}
                {n.dumps.length > 0 && <span className="badge">dumped{n.dumps.length > 1 ? ` ×${n.dumps.length}` : ""}</span>}
                {rules.map((rule) => (
                  <Badge key={rule} live={live} sev={sevOf(cite, n.yaraHits.filter((y) => y.rule === rule).map((y) => y.seq))}>
                    yara{" "}
                    <span className="val" translate="no">
                      {rule}
                    </span>
                  </Badge>
                ))}
                {r.kids && !r.expanded && (
                  <button
                    type="button"
                    tabIndex={-1}
                    className="border-0 border-b border-dotted border-ink-mute bg-transparent p-0 font-mono text-micro text-ink-soft hover:border-solid hover:text-ink"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggle(n, true);
                    }}
                  >
                    + {n.children.length} {n.children.length === 1 ? "child" : "children"}
                  </button>
                )}
              </div>
              {cmd && (
                <div className={cx("node-cmd", compact && (isSel ? "line-clamp-none" : "line-clamp-1"))} translate="no" title={n.cmdline || n.image}>
                  {cmd}
                </div>
              )}
            </div>
            {compact ? (
              <div className="node-meta w-[clamp(108px,28%,144px)] py-1.5 pl-2 max-[720px]:col-auto max-[720px]:p-[6px_10px_6px_8px]" role="gridcell">
                {outside ? (
                  <span className="exit mt-0">{n.isContext ? "parent" : "not traced"}</span>
                ) : (
                  <>
                    <span className="exit mt-0 truncate">{lifeText(n, live, nowT, collected, true)}</span>
                    {life}
                  </>
                )}
              </div>
            ) : (
              <div className="node-meta" role="gridcell">
                {n.isContext ? (
                  job.kind === "url" ? "parent of the traced root" : "parent of the sample"
                ) : n.external ? (
                  "outside the traced subtree"
                ) : (
                  <>
                    {acts.length > 0 && (
                      <div className="acts">
                        {acts.map(([k, c, word]) => (
                          <span key={k}>
                            <CatMark c={c} />
                            {count(n.acts[k])} {word}
                          </span>
                        ))}
                      </div>
                    )}
                    {life}
                    <span className="exit">{lifeText(n, live, nowT, collected)}</span>
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** The axis end: the collection time in a document; live, the timeout, or "now" once the run is past it. */
function TreeAxis({ tree, live, timeoutMs }: { tree: Tree; live: boolean; timeoutMs: number }) {
  const end = !live ? `+${dur(tree.axis.to)}` : timeoutMs > 0 && tree.axis.to <= timeoutMs + 999 ? `timeout ${clock(timeoutMs)}` : `now ${clock(tree.axis.to)}`;
  return (
    <div className="tree-axis" aria-hidden="true">
      <span>{tree.axis.from < 0 ? rel(tree.axis.from, 1) : "T0"}</span>
      <span>{end}</span>
    </div>
  );
}

export function ProcessTreeLive({ ctx, className }: { ctx: ReportCtx; className?: string }) {
  const tree = treeOf(ctx);
  const [open, setOpen] = useState<string | undefined>();
  if (tree.inScope === 0)
    return (
      <div className={cx("border-t border-rule-strong", className)}>
        <Empty inline rule={false}>
          No process from the sample yet.
        </Empty>
      </div>
    );
  return (
    <div className={cx("flex min-h-0 flex-col", className)}>
      <TreeGrid ctx={ctx} tree={tree} compact selected={open} onSelect={(n) => setOpen((k) => (k === n.key ? undefined : n.key))} className="min-h-0 overflow-y-auto" />
      <TreeAxis tree={tree} live={ctx.mode === "live"} timeoutMs={(Number(ctx.job.timeout_secs) || 0) * 1000} />
    </div>
  );
}

export default function ProcessTreeFig({ ctx, no }: FigProps) {
  const job = ctx.job;
  const live = ctx.mode === "live";
  const tree = treeOf(ctx);
  const standalone = useStandaloneLens(job.id);
  const selected = resolveSelection(tree, useSelectedKey(job.id));

  const inScope = tree.inScope;
  const inj = tree.injections.length;
  const countText = `${count(inScope)} ${inScope === 1 ? "process" : "processes"}${inj ? ` · ${count(inj)} ${inj === 1 ? "injection" : "injections"}` : ""}`;

  return (
    <>
      <section className="cell s8 fig" aria-labelledby={figTitleId(no)}>
        <FigHead no={no} label="Process tree" count={countText} />
        {tree.order.length === 0 ? (
          <Empty inline>No process activity</Empty>
        ) : (
          <div className="fig-body">
            <TreeGrid ctx={ctx} tree={tree} selected={selected?.key} onSelect={(n) => selectProcess(job.id, n.key)} />
            <TreeAxis tree={tree} live={live} timeoutMs={(Number(ctx.job.timeout_secs) || 0) * 1000} />
          </div>
        )}
      </section>
      {!standalone && <ProcessLensView ctx={ctx} tree={tree} node={selected} />}
    </>
  );
}
